import { useCallback, useEffect, useMemo, useState } from "react";
import {
  foyerApi,
  type LinkRoomMapping,
  type LinkRoomMappingState,
} from "../http/foyer";
import { useTranslation } from "react-i18next";
import { refusalCopy } from "../components/link/linkRefusalCopy";
import type { Formatters } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Which PMS unit each room of the hotel is (`MEWS-4`), for the Units page: its
 * PMS column and the unit drawer's mapping field. The mapping used to be a
 * table on PMS → Integrations; it lives with the unit now, where the person
 * editing room 214 already is.
 *
 * Pass `premiseId` only for someone who may see the PMS link (`pms-link`,
 * Nenda-only): everyone else gets `connection: null` and the Units page shows
 * nothing about a PMS.
 */

export interface PmsUnit {
  unitRef: string;
  name: string;
}

export interface PmsRoomRow {
  roomId: string;
  state: LinkRoomMappingState;
  unitRef: string | null;
  unitName: string | null;
}

export interface RoomMapping {
  /** The active connection that lists units, or null: show nothing. */
  connection: { id: string; vendor: string; displayName: string } | null;
  /** The vendor answered the unit list; false shows `problem` instead. */
  ok: boolean;
  problem: string | null;
  units: PmsUnit[];
  rowFor: (roomId: string) => PmsRoomRow | null;
  /** Several units share a name in Mews (the demo has many "1"): those carry
   *  the tail of their id so they can be told apart. */
  labelOf: (unit: PmsUnit) => string;
  proposed: PmsRoomRow[];
  reload: () => Promise<void>;
}

export function useRoomMapping(premiseId: string | undefined): RoomMapping {
  const { t } = useTranslation(["premises", "common"]);
  const [data, setData] = useState<LinkRoomMapping | null>(null);

  const reload = useCallback(async () => {
    if (!premiseId) {
      setData(null);
      return;
    }
    try {
      setData(await foyerApi.linkRoomMapping(premiseId));
    } catch {
      // No answer is no PMS column rather than a broken Units page.
      setData(null);
    }
  }, [premiseId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return useMemo(() => {
    const units = data?.units ?? [];
    const rows = data?.rows ?? [];
    const counts = new Map<string, number>();
    for (const u of units) counts.set(u.name, (counts.get(u.name) ?? 0) + 1);
    const byRoom = new Map(rows.map((r) => [r.roomId, r]));
    return {
      connection: data?.connection ?? null,
      ok: data?.ok === true,
      problem:
        data?.connection && data.ok !== true
          ? refusalCopy(t, data.code ?? null) || t("roomMapping.listFailed")
          : null,
      units,
      rowFor: (roomId: string) => byRoom.get(roomId) ?? null,
      labelOf: (u: PmsUnit) =>
        (counts.get(u.name) ?? 0) > 1
          ? `${u.name} (…${u.unitRef.slice(-4)})`
          : u.name,
      proposed: rows.filter((r) => r.state === "proposed"),
      reload,
    };
  }, [data, reload, t]);
}

const CHECK_KEY: Record<string, string> = {
  no_stay: "noStay",
  unmapped: "unmapped",
  ambiguous_room: "ambiguousRoom",
};

/** "Check guest": who a charge for this room would reach, in one sentence.
 *  Dates and flags only; the answer never names the guest. */
export async function checkGuest(
  t: Translate,
  fmt: Formatters,
  premiseId: string,
  roomId: string,
  displayName: string
): Promise<string> {
  const key = (name: string) => `premises:roomMapping.${name}`;
  try {
    const body = await foyerApi.linkRoomStay(premiseId, roomId);
    if (body.ok && body.stay) {
      const day = { day: "numeric", month: "short" } as const;
      const params = {
        arrival: fmt.date(body.stay.arrival, day),
        departure: fmt.date(body.stay.departure, day),
        name: displayName,
      };
      return translateKey(
        t,
        !body.stay.chargeableChecked
          ? key("stay.chargeUnknown")
          : !body.stay.chargeable
            ? key("stay.chargeBlocked")
            : key("stay.checkedIn"),
        params
      );
    }
    const check = CHECK_KEY[body.code ?? ""];
    if (check) return translateKey(t, key(`checks.${check}`));
    return refusalCopy(t, body.code) || translateKey(t, key("checkFailed"));
  } catch {
    return translateKey(t, key("serverUnreachable"));
  }
}
